'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import * as React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '../../../lib/utils'
import { ARROW_UP_ICON } from '../../App.icons'

interface Props {
  clampedTextHeight: number
  isDisabled?: boolean
  backgroundColor?: string
}

const OVERLAY_HEIGHT_IN_PX = 24

export const ExpandableContent = ({
  children,
  clampedTextHeight,
  isDisabled = false,
  backgroundColor = 'white',
}: React.PropsWithChildren<Props>) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isBrowser, setIsBrowser] = useState(false)
  const [clientHeight, setClientHeight] = useState(-1)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const isTextClamped = clientHeight > clampedTextHeight

  const t = useTranslations('expandable-content')

  const toggleCollapse = () => {
    if (isDisabled) return
    setIsCollapsed(!isCollapsed)
  }

  useLayoutEffect(() => {
    if (!ref.current) return
    if (ref.current.clientHeight > clampedTextHeight) {
      setClientHeight(ref.current.clientHeight + OVERLAY_HEIGHT_IN_PX)
    }
  }, [])

  useEffect(() => {
    setIsBrowser(true)
  }, [])

  if (isTextClamped) {
    return (
      <div className="relative flex flex-col gap-2">
        <div
          className={cn('overflow-hidden transition-all', !isCollapsed && 'pb-6')}
          style={isCollapsed ? { maxHeight: `${clampedTextHeight}px` } : { maxHeight: `${clientHeight}px` }}
        >
          {children}
        </div>
        <button
          onClick={toggleCollapse}
          style={{ height: OVERLAY_HEIGHT_IN_PX, backgroundColor }}
          className="absolute inset-x-0 bottom-0 z-10 flex w-full cursor-pointer items-center gap-1 pt-2 text-sm font-medium"
        >
          {isCollapsed ? t('see-more-content') : t('see-less-content')}
          <Image
            src={ARROW_UP_ICON}
            className={cn('transition-all', isCollapsed && '-rotate-180')}
            alt=""
            width={16}
            height={16}
          />
        </button>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden" style={{ maxHeight: `${clampedTextHeight}px` }}>
      <div ref={ref}>{children}</div>
      {!isBrowser && (
        <div
          className="absolute inset-x-0 bottom-0 z-10 flex"
          style={{ height: OVERLAY_HEIGHT_IN_PX, backgroundColor }}
        />
      )}
    </div>
  )
}
